<template>
  <div class="mx-jersey-roster">
    <div id="header-placeholder"></div>

    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p>Loading design...</p>
      </div>
    </div>

    <transition name="toast">
      <div v-if="toastMsg" :class="['toast', toastError ? 'toast-error' : 'toast-success']">
        {{ toastMsg }}
      </div>
    </transition>

    <div class="container">
      <div class="page-layout">
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            :view-only="true"
            @model-loaded="onModelLoaded"
          />
        </div>

        <div class="right-panel">
          <div class="panel-block roster-block">
            <RosterManager ref="rosterManager" />
          </div>
        </div>
      </div>
    </div>

    <div id="footer-placeholder"></div>

    <div class="floating-contact" id="floatingContact">
      <button class="contact-toggle" @click="toggleContactMenu" :aria-expanded="contactMenuOpen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      </button>
      <div class="contact-options" :class="{ 'open': contactMenuOpen }">
        <a href="https://wa.me/8613537631227" target="_blank" class="contact-option whatsapp"><i class="fab fa-whatsapp"></i><span>WhatsApp</span></a>
        <a href="mailto:68534150@QQ.com" class="contact-option email"><i class="fas fa-envelope"></i><span>Email</span></a>
        <a href="tel:+8613537631227" class="contact-option phone"><i class="fas fa-phone"></i><span>Call</span></a>
      </div>
    </div>
  </div>
</template>

<script>
import { useDesignStore } from '@/stores/designStore'
import { mapActions } from 'pinia'
import Viewer3D from '@/components/Viewer3D.vue'
import RosterManager from '@/components/RosterManager.vue'

export default {
  name: 'mxjerseyroster',
  components: { Viewer3D, RosterManager },

  data() {
    return {
      isLoading: false,
      toastMsg: '',
      toastError: false,
      _toastTimer: null,
      contactMenuOpen: false,
    }
  },

  mounted() {
    const store = useDesignStore()
    store.currentGarment = 'Mx Jersey'
    
    this.loadHeaderFooter()
    this.setupOutsideClick()
    
    // 先初始化数据，再加载模型
    this.$nextTick(() => {
        this.init()
    })
    },

  methods: {
    ...mapActions(useDesignStore, ['loadDesignFromData']),

    loadHeaderFooter() {
      fetch('/header.html').then(r => r.text()).then(d => { document.getElementById('header-placeholder').innerHTML = d })
      fetch('/footer.html').then(r => r.text()).then(d => { document.getElementById('footer-placeholder').innerHTML = d })
    },

    async init() {
        const params = new URLSearchParams(window.location.search)
        const id = params.get('id')
        
        const store = useDesignStore()
        store.currentGarment = 'Mx Jersey'
        
        if (!id) {
            await this.$nextTick()
            if (this.$refs.viewer3d) {
            this.$refs.viewer3d.loadGarment('Mx Jersey')
            }
            return
        }

        this.isLoading = true
        
        try {
            // 先加载设计数据
            await store.loadSharedDesign(id, {
            targetGarment: 'Mx Jersey',
            loadComments: false,
            })
            
            // 数据加载完成后，再加载模型
            if (this.$refs.viewer3d) {
            this.$refs.viewer3d.loadGarment('Mx Jersey')
            
            // 等待模型加载完成
            await new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                if (this.$refs.viewer3d.currentModel && this.$refs.viewer3d.partsMap.size > 0) {
                    clearInterval(checkInterval)
                    resolve()
                }
                }, 100)
                setTimeout(() => { clearInterval(checkInterval); resolve() }, 5000)
            })
            }
            
            // 强制合成纹理
            await this.$nextTick()
            if (this.$refs.viewer3d) {
            // 重新应用颜色和纹理
            this.$refs.viewer3d.applyColorsToModel()
            
            for (const [part, layers] of store.partLayersMap.entries()) {
                if (layers.length > 0 && this.$refs.viewer3d.partsMap.has(part)) {
                this.$refs.viewer3d.compositePartTexture(part)
                }
            }
            }
            
            // 刷新 RosterManager
            await this.$nextTick()
            if (this.$refs.rosterManager?.loadFromStore) {
            this.$refs.rosterManager.loadFromStore()
            }
            
        } catch (e) {
            console.error('Load error:', e)
            this.showToast('Failed to load design', true)
        } finally {
            this.isLoading = false
        }
        },

    async onModelLoaded() {
      const store = useDesignStore()
      const viewer = this.$refs.viewer3d
      if (!viewer) return

      if (typeof viewer.applyColorsToModel === 'function') viewer.applyColorsToModel()

      for (const [part, layers] of store.partLayersMap.entries()) {
        if (!layers || layers.length === 0) continue
        if (!viewer.partsMap || !viewer.partsMap.has(part)) continue

        for (const layer of layers) {
          if (!layer.isText && layer.url && !layer.img) {
            await new Promise((resolve) => {
              const img = new Image()
              img.crossOrigin = 'anonymous'
              img.onload = () => { layer.img = img; resolve() }
              img.onerror = () => resolve()
              img.src = layer.url
            })
          }
          if (layer.isText && layer.textContent && !layer.img) {
            const canvas = store._createTextCanvas(
              layer.textContent, layer.textFont || 'Arial',
              layer.textColor || '#000000', layer.fontSize || 80
            )
            const img = new Image()
            img.src = canvas.toDataURL()
            layer.img = img
          }
        }
        viewer.compositePartTexture(part, null, false)
      }
    },

    showToast(msg, isError = false) {
      this.toastMsg = msg
      this.toastError = isError
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMsg = '' }, 3000)
    },

    toggleContactMenu() { this.contactMenuOpen = !this.contactMenuOpen },
    setupOutsideClick() {
      document.addEventListener('click', (e) => {
        const container = document.getElementById('floatingContact')
        if (container && !container.contains(e.target)) this.contactMenuOpen = false
      })
    }
  }
}
</script>

<style scoped>
.mx-jersey-roster { font-family: 'Inter', sans-serif; background: #ffffff; color: #333; min-height: 100vh; }
.loading-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); display: flex; align-items: center; justify-content: center; z-index: 10000; }
.loading-inner { text-align: center; color: #fff; }
.spinner { width: 40px; height: 40px; border: 4px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 12px; }
@keyframes spin { to { transform: rotate(360deg); } }
.toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 8px 20px; border-radius: 8px; font-size: 0.85rem; font-weight: 500; color: #fff; z-index: 10001; pointer-events: none; }
.toast-success { background: #10b981; }
.toast-error { background: #ef4444; }
.toast-enter-active, .toast-leave-active { transition: opacity 0.3s, transform 0.3s; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
.container { width: 95%; max-width: 1400px; margin: 0 auto; padding: 1rem 0; }
.page-layout { display: flex; gap: 2rem; flex-wrap: wrap; align-items: flex-start; }
.viewer-wrapper { flex: 1; min-width: 280px; }
.right-panel { flex: 1; min-width: 320px; display: flex; flex-direction: column; gap: 0.8rem; }
.panel-block { background: #f5f5f5; border-radius: 10px; padding: 1rem; }
.roster-block { background: #f0fdf4; }
.floating-contact { position: fixed; bottom: 80px; right: 20px; z-index: 1000; }
.contact-toggle { width: 56px; height: 56px; border-radius: 50%; background: #25d366; color: white; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(37,211,102,0.4); transition: all 0.3s ease; }
.contact-toggle:hover { transform: scale(1.1); }
.contact-options { position: absolute; bottom: 70px; right: 0; display: flex; flex-direction: column; gap: 6px; opacity: 0; visibility: hidden; transform: translateY(10px); transition: all 0.3s ease; }
.contact-options.open { opacity: 1; visibility: visible; transform: translateY(0); }
.contact-option { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background: white; border-radius: 24px; text-decoration: none; color: #333; font-size: 0.85rem; font-weight: 500; box-shadow: 0 2px 8px rgba(0,0,0,0.1); transition: all 0.2s; white-space: nowrap; }
.contact-option:hover { transform: translateX(-4px); }
.contact-option.whatsapp { color: #25d366; }
.contact-option.email { color: #ea4335; }
.contact-option.phone { color: #4285f4; }
@media (max-width: 900px) { .page-layout { flex-direction: column; } .viewer-wrapper, .right-panel { width: 100%; min-width: 0; } }
@media (max-width: 850px) { .container { width: 100%; padding: 0.5rem; } .panel-block { padding: 0.6rem; } .floating-contact { bottom: 70px; right: 12px; } .contact-toggle { width: 48px; height: 48px; } }
</style>